import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { PageHeader, EmptyState, Field } from "../components/Shared";
import { PAYSET } from "../constants/testIds";
import { Plus, Star, Power, Trash2, Pencil } from "lucide-react";

export default function PaymentSettings() {
  const { t } = useLang();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // account object or "new"

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payment-accounts");
      setRows(data.data);
    } catch (e) { toast.error(extractError(e).message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      await api.post(`/admin/payment-accounts/${id}/${path}`);
      toast.success(t("paymentSettings.saved"));
      load();
    } catch (e) { toast.error(extractError(e).message); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("paymentSettings.confirmDelete"))) return;
    try {
      await api.delete(`/admin/payment-accounts/${id}`);
      toast.success(t("paymentSettings.deleted"));
      load();
    } catch (e) { toast.error(extractError(e).message); }
  };

  return (
    <div data-testid={PAYSET.page} className="space-y-8">
      <PageHeader eyebrow={t("payments.eyebrow")} title={t("paymentSettings.title")} subtitle={t("paymentSettings.subtitle")}>
        <Button data-testid={PAYSET.addBtn} onClick={() => setEditing("new")} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white h-11 font-semibold">
          <Plus className="h-4 w-4 mr-1" /> {t("paymentSettings.add")}
        </Button>
      </PageHeader>

      {editing && (
        <AccountForm
          account={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => { setEditing(null); load(); }}
        />
      )}

      <div>
        <h2 className="text-base md:text-lg font-semibold text-white mb-3">{t("paymentSettings.sectionTitle")}</h2>
        {rows.length === 0 ? (
          <EmptyState testId={PAYSET.empty} text={t("paymentSettings.empty")} />
        ) : (
          <div className="space-y-3">
            {rows.map((a) => (
              <Card key={a.account_id} data-testid={`payment-account-row-${a.account_id}`} className="bg-[#161923] border-white/5">
                <CardContent className="p-5 flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white">{a.bank_name}</span>
                      <span data-testid={`payment-account-number-${a.account_id}`} className="font-mono text-emerald-300">{a.account_number}</span>
                      {a.is_default && (
                        <span data-testid={`payment-account-default-${a.account_id}`} className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-amber-300">
                          <Star className="h-3 w-3" /> {t("paymentSettings.default")}
                        </span>
                      )}
                      <span data-testid={`payment-account-status-${a.account_id}`} className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${a.is_active ? "border-emerald-500/30 bg-emerald-500/15 text-emerald-300" : "border-slate-500/30 bg-slate-500/15 text-slate-400"}`}>
                        {a.is_active ? t("paymentSettings.active") : t("paymentSettings.inactive")}
                      </span>
                    </div>
                    <div className="text-sm text-slate-400 mt-1">
                      {a.account_holder_name}{a.display_label ? ` · ${a.display_label}` : ""}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button data-testid={`payment-account-edit-${a.account_id}`} onClick={() => setEditing(a)} variant="ghost" className="text-slate-300 hover:bg-white/5 h-9">
                      <Pencil className="h-4 w-4 mr-1" /> {t("common.edit")}
                    </Button>
                    {!a.is_default && a.is_active && (
                      <Button data-testid={`payment-account-set-default-${a.account_id}`} onClick={() => act(a.account_id, "set-default", t("paymentSettings.confirmDefault"))} variant="ghost" className="text-amber-300 hover:bg-amber-500/10 h-9">
                        <Star className="h-4 w-4 mr-1" /> {t("paymentSettings.setDefault")}
                      </Button>
                    )}
                    {a.is_active ? (
                      <Button data-testid={`payment-account-deactivate-${a.account_id}`} onClick={() => act(a.account_id, "deactivate")} variant="ghost" className="text-slate-400 hover:bg-white/5 h-9">
                        <Power className="h-4 w-4 mr-1" /> {t("paymentSettings.deactivate")}
                      </Button>
                    ) : (
                      <Button data-testid={`payment-account-activate-${a.account_id}`} onClick={() => act(a.account_id, "activate")} variant="ghost" className="text-emerald-300 hover:bg-emerald-500/10 h-9">
                        <Power className="h-4 w-4 mr-1" /> {t("paymentSettings.activate")}
                      </Button>
                    )}
                    <Button data-testid={`payment-account-delete-${a.account_id}`} onClick={() => remove(a.account_id)} variant="ghost" className="text-red-400 hover:bg-red-500/10 h-9">
                      <Trash2 className="h-4 w-4 mr-1" /> {t("common.delete")}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountForm({ account, onClose, onDone }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    bank_name: account?.bank_name || "",
    account_number: account?.account_number || "",
    account_holder_name: account?.account_holder_name || "",
    display_label: account?.display_label || "",
    is_active: account ? account.is_active : true,
    is_default: account?.is_default || false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!account && form.is_default && !window.confirm(t("paymentSettings.confirmDefault"))) return;
    setBusy(true);
    try {
      if (account) {
        await api.patch(`/admin/payment-accounts/${account.account_id}`, {
          bank_name: form.bank_name, account_number: form.account_number,
          account_holder_name: form.account_holder_name, display_label: form.display_label || null,
        });
      } else {
        await api.post("/admin/payment-accounts", { ...form, display_label: form.display_label || null });
      }
      toast.success(t("paymentSettings.saved"));
      onDone();
    } catch (err) { toast.error(extractError(err).message); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid={PAYSET.form} className="bg-[#10121B] border-[#FF3B30]/30">
      <CardContent className="p-6">
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("paymentSettings.bankName")} testId={PAYSET.bank} value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} required minLength={2} />
          <Field label={t("paymentSettings.accountNumber")} testId={PAYSET.number} value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} required minLength={4} />
          <Field label={t("paymentSettings.holderName")} testId={PAYSET.holder} value={form.account_holder_name} onChange={(e) => setForm({ ...form, account_holder_name: e.target.value })} required minLength={2} />
          <Field label={t("paymentSettings.displayLabel")} testId={PAYSET.label} value={form.display_label} onChange={(e) => setForm({ ...form, display_label: e.target.value })} />
          {!account && (
            <>
              <div className="space-y-1.5">
                <label className="text-slate-300 text-sm">{t("paymentSettings.status")}</label>
                <select data-testid={PAYSET.status} value={form.is_active ? "ACTIVE" : "INACTIVE"} onChange={(e) => setForm({ ...form, is_active: e.target.value === "ACTIVE" })} className="w-full h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
                  <option value="ACTIVE">{t("paymentSettings.active")}</option>
                  <option value="INACTIVE">{t("paymentSettings.inactive")}</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-slate-300 text-sm">{t("paymentSettings.default")}</label>
                <select data-testid={PAYSET.defaultSelect} value={form.is_default ? "YES" : "NO"} onChange={(e) => setForm({ ...form, is_default: e.target.value === "YES" })} className="w-full h-10 rounded-md bg-[#1C202E] border border-white/10 text-white text-sm px-3">
                  <option value="NO">{t("common.no")}</option>
                  <option value="YES">{t("common.yes")}</option>
                </select>
              </div>
            </>
          )}
          <div className="sm:col-span-2 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-slate-300">{t("common.cancel")}</Button>
            <Button data-testid={PAYSET.save} type="submit" disabled={busy} className="bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">{busy ? t("common.loading") : t("paymentSettings.save")}</Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
